import { TRPCClientError } from '@trpc/client'

/**
 * The most a request sent with fetch's keepalive may carry: 64 KiB, which the browser counts for
 * all the page's keepalive requests in flight together, and refuses beyond.
 */
export const KEEPALIVE_LIMIT = 64 * 1024

interface Answer {
  result?: { data?: unknown }
  error?: unknown
}

/**
 * Calls a tRPC mutation on its own with fetch's keepalive, which the browser still sends while
 * the page goes away (a tab that closes): the body is the input as JSON, as the API client's
 * httpLink sends it, to `url` (the API's path and the procedure's). Null when the body does not
 * fit in a keepalive request. While the page is still there it gives the answer, or the server's
 * refusal as the client's error (lib/errors.ts reads it).
 */
export function keepaliveMutation<T>(url: string, input: unknown): Promise<T> | null {
  const body = JSON.stringify(input)
  if (new TextEncoder().encode(body).length > KEEPALIVE_LIMIT) return null
  return fetch(url, {
    method: 'POST',
    keepalive: true,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body,
  }).then(async (res) => {
    const answer = (await res.json().catch(() => null)) as Answer | null
    if (answer?.error) throw TRPCClientError.from(answer as object)
    if (!answer?.result || !('data' in answer.result))
      throw TRPCClientError.from(new Error(`unexpected answer (${res.status})`))
    return answer.result.data as T
  })
}
